// A searchable timezone combobox. Port of public/assets/js/tz_picker.js.
//
// This existed in the PHP app on EIGHT forms and did not survive the migration
// (docs/migration-gaps.md item 4). The two places that still asked for a zone
// were left as free-text inputs where an inexact string is a hard
// 400 Unknown timezone, and `allTimezones()` in @msc/shared was left exported,
// documented "Drives the timezone picker", with no callers at all.
//
// Only the DOM lives here. The curated zone list, the search and the display
// formatting are in @msc/shared (`timezones.ts`) so they can be unit-tested —
// `web/` has no test runner, and the curated aliases are the part of this that
// was lost once already.
//
// The hidden input is the source of truth, so a form can only ever submit a
// canonical IANA id or '' (= inherit). The visible text box is unnamed and only
// searches; it is never what gets posted.
//
// jQuery is gone — the original needed it, this does not.

import { el } from '../dom.js';
import { TZ_MAX_RESULTS, tzDisplayLabel, tzPool, tzSearch, type TzChoice } from '@msc/shared';

export interface TzPickerOptions {
  /** Name for the hidden input — this is what the form submits. */
  name: string;
  /** Current value: a canonical IANA id, or '' for inherit/default. */
  value?: string | null;
  /**
   * Label for the empty-value row. The wording matters: on a group form the
   * fallback is the SITE zone, on an event form it is the GROUP zone, and those
   * are different chains (see shared/src/time.ts).
   */
  inheritLabel?: string;
  /** Zone shown in parentheses on the inherit row. */
  inheritHint?: string;
  placeholder?: string;
  /**
   * Pre-fill an EMPTY picker from the browser's zone. For create and signup
   * forms, where a sensible guess beats making someone search. Never overwrites a
   * value the user already has.
   *
   * Do NOT set this where blank means "inherit" — it would pin a zone onto every
   * record nobody chose one for. The PHP original passed it on the create-group
   * and signup forms only.
   */
  autodetect?: boolean;
  /**
   * Whether to offer the empty "inherit" row. Default true.
   *
   * Set false where "inherit" is not a concept the person filling the form can
   * understand — the signup form, where a stranger has never heard of the site
   * zone and the SPA cannot even show them what it is. With no row there is no
   * empty value, so `value()` always returns a real IANA id and the caller must
   * seed a real one.
   */
  allowInherit?: boolean;
  id?: string;
}

export interface TzPickerHandle {
  /** The element to place in the form. */
  root: HTMLElement;
  /** The current IANA id, or '' for inherit. */
  value(): string;
  set(id: string): void;
}

export function tzPicker(opts: TzPickerOptions): TzPickerHandle {
  const pool =
    opts.allowInherit === false
      ? tzPool(null)
      : tzPool(opts.inheritLabel ?? 'Site default', opts.inheritHint ?? '');
  const placeholder = opts.placeholder ?? 'Search, e.g. Mountain, Denver, MST…';

  const hidden = el('input', { type: 'hidden', name: opts.name }) as HTMLInputElement;
  hidden.value = opts.value ?? '';

  const input = el('input', {
    type: 'text',
    class: 'tz-picker-input',
    autocomplete: 'off',
    role: 'combobox',
    'aria-autocomplete': 'list',
    'aria-expanded': 'false',
    placeholder,
    ...(opts.id ? { id: opts.id } : {}),
  }) as HTMLInputElement;

  const list = el('ul', { class: 'tz-picker-list', role: 'listbox', hidden: true }) as HTMLUListElement;
  const root = el('div', { class: 'tz-picker' }, [hidden, input, list]);

  let matches: TzChoice[] = [];
  let activeIdx = -1;

  function setSelection(id: string): void {
    hidden.value = id;
    input.value = tzDisplayLabel(pool, id);
  }

  function open(): void {
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  function close(): void {
    list.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    activeIdx = -1;
  }

  function render(query: string): void {
    matches = tzSearch(pool, query, TZ_MAX_RESULTS);
    activeIdx = -1;

    if (matches.length === 0) {
      list.replaceChildren(el('li', { class: 'tz-picker-empty' }, 'No matches'));
    } else {
      const selected = hidden.value;
      list.replaceChildren(
        ...matches.map((zone, idx) =>
          el(
            'li',
            {
              class: `tz-picker-opt${zone.id === selected ? ' is-selected' : ''}`,
              role: 'option',
              'data-idx': String(idx),
              'data-id': zone.id,
            },
            // Text nodes, never innerHTML.
            zone.hint ? [zone.label, ' ', el('small', {}, zone.hint)] : [zone.label],
          ),
        ),
      );
    }
    open();
  }

  function paintActive(): void {
    for (const node of list.querySelectorAll('.tz-picker-opt')) {
      node.classList.remove('is-active');
    }
    const active = list.querySelector(`.tz-picker-opt[data-idx="${activeIdx}"]`);
    if (active) {
      active.classList.add('is-active');
      active.scrollIntoView({ block: 'nearest' });
    }
  }

  // Only when the picker starts empty, and only for a zone the pool actually
  // contains — a browser reporting something non-canonical must not become the
  // stored value.
  if (opts.autodetect && !hidden.value) {
    try {
      const guess = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (guess && pool.some((z) => z.id === guess)) hidden.value = guess;
    } catch {
      /* a browser without a resolvable zone just gets the inherit row */
    }
  }
  setSelection(hidden.value);

  input.addEventListener('focus', () => {
    input.select();
    render('');
  });
  input.addEventListener('input', () => render(input.value));

  input.addEventListener('keydown', (ev) => {
    if (list.hidden) {
      if (ev.key === 'ArrowDown') render(input.value);
      return;
    }
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      activeIdx = Math.min(activeIdx + 1, matches.length - 1);
      paintActive();
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      activeIdx = Math.max(activeIdx - 1, 0);
      paintActive();
    } else if (ev.key === 'Enter') {
      const pick = activeIdx >= 0 ? matches[activeIdx] : undefined;
      if (pick) {
        // Only swallow Enter when it is actually choosing something, so Enter
        // still submits the form when the list is open but nothing is highlighted.
        ev.preventDefault();
        setSelection(pick.id);
        close();
      }
    } else if (ev.key === 'Escape') {
      setSelection(hidden.value);
      close();
    }
  });

  // Tabbing or clicking away abandons whatever was typed and restores the box to
  // the committed value. Safe next to option selection only because options use
  // mousedown + preventDefault below, which keeps focus on the input so picking
  // never fires this.
  input.addEventListener('blur', () => {
    setSelection(hidden.value);
    close();
  });

  list.addEventListener('mousedown', (ev) => {
    const target = (ev.target as HTMLElement | null)?.closest('.tz-picker-opt');
    if (!target) return;
    ev.preventDefault(); // keep focus on the input — see the blur handler
    setSelection(target.getAttribute('data-id') ?? '');
    close();
  });

  document.addEventListener('mousedown', (ev) => {
    if (!root.contains(ev.target as Node)) {
      setSelection(hidden.value);
      close();
    }
  });

  // A native form reset restores the hidden input to its value ATTRIBUTE but
  // clears the unnamed search box, leaving the two disagreeing. The modal calls
  // reset() on close, so without this the box comes back blank.
  //
  // Bound on the document rather than on the owning form, because the picker is
  // built before it is inserted anywhere — a closest('form') here would always
  // be null. `reset` bubbles, so this sees it either way, and the containment
  // check keeps one form's reset from resyncing another form's picker.
  document.addEventListener('reset', (ev) => {
    const form = ev.target as Node | null;
    if (!form || !form.contains(root)) return;
    // After the browser's own reset has run, not before.
    setTimeout(() => {
      setSelection(hidden.value);
      close();
    }, 0);
  });

  return {
    root,
    value: () => hidden.value,
    set: setSelection,
  };
}
